import type { FunctionComponent, SVGProps } from 'react'

import FolderIcon from '@/assets/svgs/folder.svg?react'
import HistoryIcon from '@/assets/svgs/history-route.svg?react'
import HomeIcon from '@/assets/svgs/home-route.svg?react'
import InfoIcon from '@/assets/svgs/info.svg?react'
import NewIcon from '@/assets/svgs/new-route.svg?react'
import PlaygroundHistoryIcon from '@/assets/svgs/playground-history-route.svg?react'
import PlaygroundIcon from '@/assets/svgs/playground-route.svg?react'
import PocIcon from '@/assets/svgs/poc-route.svg?react'
import ReferenceHistoryIcon from '@/assets/svgs/reference-history-route.svg?react'
import ReferenceIcon from '@/assets/svgs/reference-route.svg?react'
import SettingsIcon from '@/assets/svgs/settings.svg?react'
import StackIcon from '@/assets/svgs/stack.svg?react'
import CharacterIcon from '@/assets/svgs/tools/character.svg?react'
import UserIcon from '@/assets/svgs/user.svg?react'
import VideoGenerationIcon from '@/assets/svgs/video-generation-route.svg?react'
import VideoHistoryIcon from '@/assets/svgs/video-history-route.svg?react'

import { imageUtilities, referenceTools, tools } from './tools'

export type RouteIcon = FunctionComponent<SVGProps<SVGSVGElement>>

export interface AppRoute {
  to: string
  label: string
  icon: RouteIcon
  /** Also highlight the nav item on pages below this route. */
  matchPrefix?: boolean
  /**
   * Stages (`VITE_STAGE`) where the navbar and command palette leave this item
   * out. Visibility only: the route still exists and opens by URL.
   */
  hiddenOn?: readonly string[]
}

export interface CommandPaletteRoute extends AppRoute {
  group: 'Home' | 'Workspace' | 'Playground' | 'Reference Playground' | 'Utilities' | 'Video' | 'Account' | 'Settings'
  available: boolean
}

const WORKSPACE_ROUTES: AppRoute[] = [
  { to: '/', label: 'Home', icon: HomeIcon },
  { to: '/workflow/new', label: 'New Workflow', icon: NewIcon },
  { to: '/workflow-history', label: 'Workflow History', icon: HistoryIcon }
]

const PLAYGROUND_ROUTES: AppRoute[] = [
  { to: '/playground', label: 'Playground', icon: PlaygroundIcon },
  { to: '/playground-history', label: 'Playground History', icon: PlaygroundHistoryIcon }
]

const REFERENCE_ROUTES: AppRoute[] = [
  { to: '/reference-playground', label: 'Reference Playground', icon: ReferenceIcon },
  { to: '/reference-images-history', label: 'Reference Images History', icon: ReferenceHistoryIcon }
]

const VIDEO_ROUTES: AppRoute[] = [
  { to: '/video-generation', label: 'Video Generation', icon: VideoGenerationIcon },
  { to: '/video-generation-history', label: 'Video History', icon: VideoHistoryIcon }
]

/** The /poc index; its entries live in `POC_DIRECTORY` (`pocRoutes.ts`). */
const POC_ROUTES: AppRoute[] = [
  { to: '/poc', label: 'POCs', icon: PocIcon, matchPrefix: true, hiddenOn: ['dev', 'stg', 'prd'] }
]

const SETTINGS_ROUTES: AppRoute[] = [
  { to: '/folder/list', label: 'Folders', icon: StackIcon },
  { to: '/settings', label: 'Settings', icon: SettingsIcon }
]

/**
 * The navbar manifest, top to bottom; each inner array is a group. Hide an item
 * on some stages with `hiddenOn`, then read it through `navRouteGroupsForStage`.
 */
export const NAV_ROUTE_GROUPS: AppRoute[][] = [
  WORKSPACE_ROUTES,
  PLAYGROUND_ROUTES,
  REFERENCE_ROUTES,
  VIDEO_ROUTES,
  POC_ROUTES,
  SETTINGS_ROUTES
]

/** With no stage (Storybook, tests, an unset `VITE_STAGE`), nothing is hidden. */
export function isRouteVisibleOn(route: Pick<AppRoute, 'hiddenOn'>, stage: string | undefined): boolean {
  return stage === undefined || !route.hiddenOn?.includes(stage)
}

/** The navbar groups for one stage, without the items hidden there or the groups that leaves empty. */
export function navRouteGroupsForStage(groups: AppRoute[][], stage: string | undefined): AppRoute[][] {
  return groups
    .map((group) => group.filter((route) => isRouteVisibleOn(route, stage)))
    .filter((group) => group.length > 0)
}

const [homeRoute, ...remainingWorkspaceRoutes] = WORKSPACE_ROUTES

export const COMMAND_PALETTE_ROUTES: CommandPaletteRoute[] = [
  ...(homeRoute ? [{ ...homeRoute, group: 'Home' as const, available: true }] : []),
  ...tools.map((tool) => ({
    to: tool.href,
    label: tool.title,
    icon: tool.icon,
    group: 'Playground' as const,
    available: !tool.disabled
  })),
  ...PLAYGROUND_ROUTES.map((route) => ({ ...route, group: 'Playground' as const, available: true })),
  ...remainingWorkspaceRoutes.map((route) => ({ ...route, group: 'Workspace' as const, available: true })),
  { to: '/folder/new', label: 'New Folder', icon: FolderIcon, group: 'Workspace', available: true },
  ...referenceTools.map((tool) => ({
    to: tool.href,
    label: tool.title,
    icon: tool.icon,
    group: 'Reference Playground' as const,
    available: !tool.disabled
  })),
  ...REFERENCE_ROUTES.map((route) => ({ ...route, group: 'Reference Playground' as const, available: true })),
  ...imageUtilities.map((tool) => ({
    to: tool.href,
    label: tool.title,
    icon: tool.icon,
    group: 'Utilities' as const,
    available: !tool.disabled
  })),
  ...VIDEO_ROUTES.map((route) => ({ ...route, group: 'Video' as const, available: true })),
  { to: '/profile', label: 'Profile', icon: UserIcon, group: 'Account', available: true },
  ...SETTINGS_ROUTES.map((route) => ({ ...route, group: 'Settings' as const, available: true })),
  {
    to: '/settings/character-onboarding',
    label: 'Character Onboarding',
    icon: CharacterIcon,
    group: 'Settings',
    available: true
  },
  { to: '/settings/cost-tracking', label: 'Cost Tracking', icon: StackIcon, group: 'Settings', available: true },
  { to: '/settings/infrastructure', label: 'Infrastructure', icon: StackIcon, group: 'Settings', available: true },
  { to: '/settings/users', label: 'User Management', icon: UserIcon, group: 'Settings', available: true },
  { to: '/settings/changelog', label: 'Changelog', icon: InfoIcon, group: 'Settings', available: true }
]
